/**
 * BarRows — the mock's `.engine` / `.tag-row` / `.bar-row` family, one component.
 *
 * A row is: [rank] [mark] name (+ sub) │ 8px rounded TRACK with a coloured fill │
 * value, right-aligned. Three things make it a different primitive from
 * `RankedBars` rather than a variant of it:
 *
 *  1. **The bar is a track, not a bare mark.** The unfilled remainder is drawn
 *     (`VIZ.track`), so every row shows the same 0→max span and a short bar reads
 *     as "little of the whole" instead of "a small number".
 *  2. **The value lives in its own right column**, so it never collides with the
 *     bar tip and the rows' figures align vertically — which is the whole point of
 *     a leaderboard.
 *  3. **Plain DOM, not SVG.** The mock's rows are flex divs; the marks are logos
 *     and flags (real `<img>`s), the value is rich (`<b>23</b> / 100 · 10 réponses`).
 *     Laying that out in SVG would mean re-measuring text to place every span.
 *
 * `RankedBars` stays for the SVG grouped/2-series charts (Conversations) — it can do
 * things this cannot, and vice versa.
 *
 * `value: null` renders « non mesuré » in place of the track: a hole is NOT a zero,
 * so no empty track is drawn either (an empty track reads as a measured 0).
 * Sorting is the caller's job — data arrives ordered.
 */

import type { ReactNode } from 'react';
import { Box, Typography } from '@mui/material';
import { VIZ } from './tokens';

export interface BarRow {
  /** Stable row identity — two rows may share a label (two unnamed targets). */
  key: string;
  label: string;
  /**
   * Rich label, when the identifier is not prose: an id code in a mono chip, a
   * provisional marker. `label` is still required — it is the tooltip and the
   * accessible text, so the plain form can never go missing.
   */
  labelNode?: ReactNode;
  /** Second line under the label: « 3 prompts », « 50 réponses ». */
  sub?: string;
  value: number | null;
  /** Bar colour. Defaults to the brand violet. */
  color?: string;
  /** Leading mark: an engine logo, a flag, a favicon badge. */
  mark?: ReactNode;
  /** Right-hand column. A node so it can carry `<b>23</b> / 100 · 10 réponses`. */
  valueLabel?: ReactNode;
  /** Makes the label a link. Opens in a new tab — the view keeps its filters. */
  href?: string;
  /** Rendered muted + italic: the label is provisional, not the thing's name. */
  provisional?: boolean;
}

export interface BarRowsProps {
  rows: BarRow[];
  /** Denominator of the track. A score chart passes 100; a count chart the max. */
  maxValue?: number;
  /** Shown instead of the track when `value` is `null`. */
  unmeasuredNote?: string;
  /** Width of the label gutter. The mock uses 200px for names, 190px for tags. */
  labelWidth?: number;
  /** 1-based rank in front of each row (the mock's `.tag-rank`). */
  showRank?: boolean;
  /** Right column width. Wider for « 2 citations » than for a bare score. */
  valueWidth?: number;
  emptyNote?: string;
}

export default function BarRows({
  rows,
  maxValue,
  unmeasuredNote = 'non mesuré',
  labelWidth = 200,
  showRank = false,
  valueWidth = 96,
  emptyNote,
}: BarRowsProps) {
  if (rows.length === 0) {
    return emptyNote ? (
      <Typography sx={{ color: VIZ.muted, fontSize: 13.5, py: 1.5 }}>{emptyNote}</Typography>
    ) : null;
  }

  // A count chart has no natural ceiling, so the largest row sets it — and a single
  // row then fills the track, which is honest: it IS all of what was measured.
  const max =
    maxValue ??
    Math.max(1, ...rows.map((r) => (r.value == null ? 0 : r.value)));

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      {rows.map((r, i) => {
        const color = r.color ?? VIZ.violet;
        const nameSx = {
          // ⚠ BLOCK, always. `overflow`/`text-overflow` do NOT apply to an inline box,
          // and the link branch renders an `<a>`, which is inline by default — so a
          // long url escaped its gutter and the bar was painted over the text
          // (2026-10-09). The non-link branch is a `<div>`, which is why only linked
          // rows showed it.
          display: 'block',
          fontSize: 14,
          fontWeight: 500,
          color: r.provisional ? VIZ.muted : VIZ.ink,
          fontStyle: r.provisional ? 'italic' : undefined,
          textDecoration: r.href ? 'underline' : undefined,
          textDecorationColor: VIZ.axis,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap' as const,
        };
        return (
          <Box
            key={r.key}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              py: 1,
              borderBottom: i === rows.length - 1 ? 0 : '1px solid #F3F3F5',
            }}
          >
            {showRank && (
              <Box component="span" sx={{ width: 18, flex: 'none', fontSize: 13, color: VIZ.muted }}>
                {i + 1}
              </Box>
            )}
            <Box
              sx={{
                width: labelWidth,
                flex: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                minWidth: 0,
              }}
            >
              {r.mark}
              <Box sx={{ minWidth: 0 }}>
                {r.href ? (
                  <Typography component="a" href={r.href} target="_blank" rel="noreferrer" title={r.href} sx={nameSx}>
                    {r.labelNode ?? r.label}
                  </Typography>
                ) : (
                  <Typography component="div" title={r.label} sx={nameSx}>
                    {r.labelNode ?? r.label}
                  </Typography>
                )}
                {r.sub && (
                  <Typography component="div" sx={{ fontSize: 12, color: VIZ.muted }}>
                    {r.sub}
                  </Typography>
                )}
              </Box>
            </Box>
            {r.value == null ? (
              <Box sx={{ flex: 1, fontSize: 12, fontStyle: 'italic', color: VIZ.muted }}>
                {unmeasuredNote}
              </Box>
            ) : (
              <Box sx={{ flex: 1, height: 8, bgcolor: VIZ.track, borderRadius: '99px', overflow: 'hidden' }}>
                <Box
                  sx={{
                    display: 'block',
                    height: '100%',
                    borderRadius: '99px',
                    width: `${Math.max(0, Math.min(1, r.value / max)) * 100}%`,
                    bgcolor: color,
                  }}
                />
              </Box>
            )}
            <Box
              sx={{
                width: valueWidth,
                flex: 'none',
                textAlign: 'right',
                fontSize: 13,
                color: VIZ.muted,
                whiteSpace: 'nowrap',
                fontVariantNumeric: 'tabular-nums',
                '& b': { fontSize: 14, fontWeight: 600, color: r.value == null ? VIZ.nodata : VIZ.ink },
              }}
            >
              {r.valueLabel}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}
